import { AgGridReact } from 'ag-grid-react'; // AG Grid Component
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the grid
import './table.scss'; // New custom styles
import { RowSelectedEvent } from 'ag-grid-community';
import { ReactNode, useMemo } from 'react';
import {
    ClientSideRowModelModule,
    ModuleRegistry,
    RowSelectionModule,
    PaginationModule,
    DateFilterModule,
    NumberFilterModule,
    TextFilterModule,
} from "ag-grid-community";

ModuleRegistry.registerModules([
    PaginationModule,
    RowSelectionModule,
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
]);

interface IMobileConfig {
    logo?: {
        value?: string;
        renderer?: (data: any) => ReactNode
    };
    topLeft?: { field?: string; renderer?: (data: any) => ReactNode };
    topRight?: { field?: string; renderer?: (data: any) => ReactNode };
    bottomLeft?: { field?: string; renderer?: (data: any) => ReactNode };
    bottomRight?: { field?: string; renderer?: (data: any) => ReactNode };
    more?: { field?: string; renderer?: (data: any) => ReactNode, clickHandler?: (data: any) => void }
}

interface IProps {
    data: Array<Record<string, any>>
    header: Array<any>
    rowClickHandler?: (args: any) => void
    isRowSelectable?: any
    onSelectionChanged?: any
    fitStrategy?: 'fitCellContents' | 'fitGridWidth',
    mobileConfig?: IMobileConfig,
    checkboxSelect?: boolean;
    rowSelectionMode?: 'singleRow' | 'multiRow',
    handleRowSelected?: (params: RowSelectedEvent) => void,
    rowClassRules?: any,
    height?: string;
    isLoading?: boolean;
}

const Table = ({
    data,
    header,
    rowClickHandler,
    isRowSelectable,
    onSelectionChanged,
    checkboxSelect= false,
    fitStrategy,
    mobileConfig,
    rowSelectionMode = 'singleRow',
    handleRowSelected,
    rowClassRules,
    height = 'h-[500px]',
    isLoading = false,
}: IProps) => {
    const columnTypes = useMemo(() => {
        return {
            actions: {
                width: 80,
                suppressMenu: true,
                sortable: false,
                cellStyle: { overflow: 'visible' },
            },
        };
    }, []);

    const defaultColDef = useMemo(() => {
        return {
            flex: 1,
            minWidth: 120,
            resizable: true,
            sortable: true,
            filter: true,
            headerClass: 'bg-neutral-50/50 text-neutral-500 font-bold uppercase text-[11px] tracking-wider border-b border-neutral-200',
        };
    }, []);

    const rowSelection: any = useMemo(() => {
        return {
            mode: rowSelectionMode,
            checkboxes: checkboxSelect,
            headerCheckbox: true,
            enableClickSelection: false,
            isRowSelectable: () => isRowSelectable
        };
    }, [rowSelectionMode, checkboxSelect, isRowSelectable]);

    return (
        <div className={`w-full overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm flex flex-col ${height}`}>
            <div className={`flex-1 relative ag-theme-quartz custom-grid ${mobileConfig ? "hidden lg:block" : ""}`}>
                {isLoading && (
                    <div className='absolute inset-0 z-10 bg-white/60 backdrop-blur-[1px] flex items-center justify-center'>
                        <div className='w-8 h-8 border-4 border-[#0898a0] border-t-transparent rounded-full animate-spin' />
                    </div>
                )}
                <AgGridReact
                    rowData={data}
                    columnDefs={header}
                    defaultColDef={defaultColDef}
                    onRowClicked={(params) => rowClickHandler?.(params.data)}
                    autoSizeStrategy={{
                        type: fitStrategy || 'fitCellContents'
                    }}
                    rowClass={'cursor-pointer hover:bg-neutral-50/80 transition-colors group'}
                    rowSelection={rowSelection}
                    suppressColumnVirtualisation={true}
                    onSelectionChanged={onSelectionChanged}
                    columnTypes={columnTypes}
                    onRowSelected={handleRowSelected}
                    popupParent={typeof document !== 'undefined' ? document.body : undefined}
                    rowClassRules={rowClassRules}
                    headerHeight={48}
                    rowHeight={52}
                    animateRows={true}
                />
            </div>
            {
                mobileConfig && (
                    <div className='w-full max-w-full h-full overflow-y-auto overflow-x-hidden flex flex-col items-start justify-start lg:hidden bg-neutral-50/30 p-2'>
                        {
                            data?.map((itm, index) => (
                                <MobileCard data={itm} key={index} mobileConfig={mobileConfig} />
                            ))
                        }
                    </div>
                )
            }
        </div>
    )
};

export default Table;

const MobileCard = (props: any) => {
    const { mobileConfig } = props;
    return (
        <div className='w-full min-h-[80px] bg-white px-4 py-3 border border-neutral-200 rounded-md shadow-sm mb-3 cursor-auto flex flex-row justify-between items-center whitespace-nowrap text-nowrap'>
            {
                mobileConfig?.logo && (
                    <div className='w-11 h-11 rounded-full mr-3 shrink-0 flex items-center justify-center bg-neutral-50 border border-neutral-100 overflow-hidden'>
                        {
                            mobileConfig?.logo?.renderer ? mobileConfig?.logo?.renderer() : <img src={mobileConfig?.logo?.value} alt="" className="w-full h-full object-cover" />
                        }
                    </div>
                )
            }
            <div className='flex flex-col justify-center h-full flex-1 gap-1 overflow-hidden'>
                <div className='flex flex-row justify-between w-full items-center gap-2'>
                    <RenderCardPart
                        name="topLeft"
                        data={props.data}
                        config={mobileConfig?.topLeft}
                    />
                    <RenderCardPart
                        name="topRight"
                        data={props.data}
                        config={mobileConfig?.topRight}
                    />
                </div>
                <div className='flex flex-row justify-between w-full items-center gap-2'>
                    <RenderCardPart
                        name="bottomLeft"
                        data={props.data}
                        config={mobileConfig?.bottomLeft}
                    />
                    <RenderCardPart
                        name="bottomRight"
                        data={props.data}
                        config={mobileConfig?.bottomRight}
                    />
                </div>
            </div>
            {
                mobileConfig?.more?.clickHandler && (
                    <button 
                        className="p-2 ml-2 hover:bg-neutral-100 rounded-md transition-colors shrink-0" 
                        onClick={() => mobileConfig?.more?.clickHandler(props.data)}
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.375 3.75L11.625 9L6.375 14.25" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                )
            }
        </div>
    )
}

interface IRenderCardPart {
    name: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
    data: any,
    config: {
        field?: string;
        renderer?: (data: any) => ReactNode
    }
}
const RenderCardPart = (props: IRenderCardPart) => {
    const config = props.config;
    if (!config) {
        return null;
    }
    
    // Style mappings for card parts
    const styles = {
        topLeft: "font-bold text-neutral-800 text-sm truncate",
        topRight: "font-medium text-[#0898a0] text-sm shrink-0",
        bottomLeft: "text-neutral-500 text-xs truncate",
        bottomRight: "text-neutral-400 text-xs shrink-0"
    };

    return (
        <div className={styles[props.name]}>
            {config?.renderer ? config.renderer(props.data) : props.data[config?.field || '']}
        </div>
    )
}